import { Outlet } from 'react-router'

import { Breadcrumb } from '@ui/components/navigation'
import { ManagementPageStatus, SignupPageStatus } from '@ui/interfaces'
import { ExtraParams, RouteConfig } from '@ui/interfaces/navigation'
import { CreateOrganization, Login, Logout } from '@ui/pages/authentication'
import { RequireUser, RequireUserAndOrganization } from '@ui/pages/authentication/components'
import { ErrorBoundary } from '@ui/pages/error/ErrorBoundry'
import { GenericErrorModal, GenericErrorPage } from '@ui/pages/error/GenericError'
import { Management } from '@ui/pages/manage'
import { OnboardSource } from '@ui/pages/onboarding'
import { SetupAccount } from '@ui/pages/setupAccount/SetupAccount'
import { NavigationLayout } from '@ui/router/NavigationLayout'
import {
  AdminRoutes,
  DestinationRoutes,
  ExplorerRoutes,
  OrganizationRoutes,
  PipelineRoutes,
  ProfileRoutes,
  SettingsRoutes,
  SourceRoutes,
} from '@ui/router/routes'

export enum RouteNamesEnum {
  ROOT = 'ROOT',
  LOGIN = 'LOGIN',
  SIGNUP = 'SIGNUP',
  DEMO = 'DEMO',
  FORGOT = 'FORGOT',
  RESET = 'RESET',
  INVITATION = 'INVITATION',
  CONFIRMATION = 'CONFIRMATION',
  ONBOARDING = 'ONBOARDING',
  LOGOUT = 'LOGOUT',
  NOT_FOUND = 'NOT_FOUND',
  // Explorer Routes
  EXPLORER = 'EXPLORER',
  EVENTS_EXPLORER = 'EVENTS_EXPLORER',
  TRACES_EXPLORER = 'TRACES_EXPLORER',
  // Profile Routes
  PROFILE = 'PROFILE',
  // Data Managmment Routes
  SOURCES = 'SOURCES',
  DESTINATIONS = 'DESTINATIONS',
  // Pipeline Routes v1
  ALL_PIPELINES = 'ALL_PIPELINES',
  NEW_PIPELINE = 'NEW_PIPELINE',
  EXISTING_PIPELINE = 'EXISTING_PIPELINE',
  EXISTING_PIPELINE_NODE = 'EXISTING_PIPELINE_NODE',
  PIPELINE_ERRORS = 'PIPELINE_ERRORS',

  // Organization Routes
  MANAGE_HOME = 'MANAGE_HOME',
  MANAGE_MEMBERS = 'MANAGE_MEMBERS',
  MANAGE_INSTANCES = 'MANAGE_INSTANCES',
  INSTANCE = 'INSTANCE',
  INSTANCES = 'INSTANCES',
  INGRESS = 'INGRESS',
  SETTINGS = 'SETTINGS',
  SETTINGS_USERS = 'SETTINGS_USERS',
  SETTINGS_TEAMS = 'SETTINGS_TEAMS',

  // ACCOUNT SETUP & ONBOARDING
  SETUP_ACCOUNT = 'SETUP_ACCOUNT',

  ADMIN = 'ADMIN',

  CREATE_ORGANIZATION = 'CREATE_ORGANIZATION',
}

const explorerRoutes = ExplorerRoutes()
const pipelineRoutes = PipelineRoutes()
const sourceRoutes = SourceRoutes()
const destinationRoutes = DestinationRoutes()
const settingsRoutes = SettingsRoutes()

export const routesConfig: RouteConfig[] = [
  {
    name: RouteNamesEnum.ROOT,
    path: '/',
    element: (
      <RequireUserAndOrganization>
        <NavigationLayout />
      </RequireUserAndOrganization>
    ),
    errorElement: <GenericErrorPage />,
    children: [
      {
        name: RouteNamesEnum.INSTANCES,
        path: 'instances',
        element: (
          <ErrorBoundary fallback={<GenericErrorModal error={new Error()} />}>
            <Outlet />
          </ErrorBoundary>
        ),
        handle: {
          crumb: (data: ExtraParams) => {
            return <Breadcrumb crumbStyle={data.crumbStyle} to={`/manage/${data.orgId}/instances`} label="Instances" />
          },
        },
        children: [
          {
            index: true,
            element: <Management initialState={ManagementPageStatus.INSTANCES} />,
          },
          {
            name: RouteNamesEnum.INSTANCE,
            path: ':instanceId',
            element: (
              <ErrorBoundary fallback={<GenericErrorModal error={new Error()} />}>
                <Outlet />
              </ErrorBoundary>
            ),
            handle: {
              crumb: (data: ExtraParams) => (
                <Breadcrumb
                  crumbStyle={data.crumbStyle}
                  to={`/manage/${data.orgId}/instances`}
                  label={data.instanceId}
                />
              ),
            },
            children: [explorerRoutes, pipelineRoutes, sourceRoutes, destinationRoutes],
          },
        ],
      },
      ProfileRoutes(),
      AdminRoutes(),
      OrganizationRoutes(),
      settingsRoutes,
    ].filter(Boolean) as RouteConfig[], // We do this to remove the admin routes
  },
  {
    name: RouteNamesEnum.SETUP_ACCOUNT,
    path: 'setup',
    element: (
      <RequireUser>
        <SetupAccount />
      </RequireUser>
    ),
  },
  {
    name: RouteNamesEnum.CREATE_ORGANIZATION,
    path: 'organizations',
    element: (
      <RequireUser>
        <CreateOrganization />
      </RequireUser>
    ),
  },
  {
    name: RouteNamesEnum.LOGIN,
    path: 'login',
    element: <Login initialState={SignupPageStatus.LOGIN} />,
  },
  {
    name: RouteNamesEnum.SIGNUP,
    path: 'signup',
    element: <Login initialState={SignupPageStatus.SIGNUP} />,
  },
  {
    name: RouteNamesEnum.DEMO,
    path: 'demo',
    element: <Login initialState={SignupPageStatus.DEMO} />,
  },
  {
    name: RouteNamesEnum.FORGOT,
    path: 'forgot',
    element: <Login initialState={SignupPageStatus.FORGOT} />,
  },
  {
    name: RouteNamesEnum.RESET,
    path: 'reset/:token',
    element: <Login initialState={SignupPageStatus.RESET} />,
  },
  {
    name: RouteNamesEnum.INVITATION,
    path: 'invitation/:token',
    element: <Login initialState={SignupPageStatus.INVITED} />,
  },
  {
    name: RouteNamesEnum.CONFIRMATION,
    path: 'confirmation/:token',
    element: <Login initialState={SignupPageStatus.CONFIRM_EMAIL} />,
  },
  {
    name: RouteNamesEnum.ONBOARDING,
    path: 'onboarding',
    element: (
      <RequireUser>
        <OnboardSource />
      </RequireUser>
    ),
  },
  {
    name: RouteNamesEnum.LOGOUT,
    path: 'logout',
    element: <Logout />,
  },
  {
    name: RouteNamesEnum.NOT_FOUND,
    path: '*',
    element: (
      <RequireUserAndOrganization>
        <NavigationLayout />
      </RequireUserAndOrganization>
    ),
  },
]
